/**
 * RichTextEditor - a tiptap WYSIWYG editor for contract and template text.
 *
 * Ported from the web_admin editor, but WITHOUT IMAGES (the contract PDF renders
 * the content as text, so no media library is needed) and with i18n tooltips.
 * The value travels as a string (HTML); the caller wraps the localisation
 * (et/en). HTML mode lets the code be edited directly - handy for the `{{...}}`
 * placeholders in templates.
 */
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import TextAlign from "@tiptap/extension-text-align";
import Underline from "@tiptap/extension-underline";
import Placeholder from "@tiptap/extension-placeholder";
import { useEffect, useState, type ReactNode } from "react";
import {
  Bold, Italic, Underline as UnderlineIcon, Strikethrough,
  AlignLeft, AlignCenter, AlignRight, AlignJustify,
  List, ListOrdered, Link as LinkIcon, Undo, Redo, Code2,
} from "lucide-react";
import { Button, cn } from "@wa/ui";
import { useT } from "../i18n/I18nProvider";
import { SelectField } from "../crm/ui";
import { StyledLink } from "./richtextExtensions";

interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  /**
   * Shown under the text, inside the same scrolling body: the mail composer's
   * signature, which belongs to the letter rather than to the window around it.
   * Not editable here. Without it the editor's shape is exactly as before.
   */
  after?: ReactNode;
}

export function RichTextEditor({ value, onChange, placeholder, className, after }: RichTextEditorProps) {
  const t = useT();
  const [htmlMode, setHtmlMode] = useState(false);
  const [htmlSource, setHtmlSource] = useState("");

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
        link: false,
        underline: false,
      }),
      Underline,
      TextAlign.configure({ types: ["heading", "paragraph"] }),
      StyledLink.configure({ openOnClick: false }),
      Placeholder.configure({ placeholder: placeholder ?? t("richtext.placeholder") }),
    ],
    content: value,
    onUpdate: ({ editor }) => onChange(editor.getHTML()),
    editorProps: { attributes: { class: "ProseMirror" } },
  });

  // Keep the editor in step with the value from outside (a locale switch or a
  // template being picked, for instance).
  useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(value, { emitUpdate: false });
    }
  }, [value, editor]);

  if (!editor) return null;

  const enterHtmlMode = () => { setHtmlSource(editor.getHTML()); setHtmlMode(true); };
  const exitHtmlMode = (save: boolean) => {
    if (save) { editor.commands.setContent(htmlSource); onChange(htmlSource); }
    setHtmlMode(false);
  };

  const setLink = () => {
    const previousUrl = editor.getAttributes("link").href;
    const url = window.prompt("URL", previousUrl);
    if (url === null) return;
    if (url === "") { editor.chain().focus().extendMarkRange("link").unsetLink().run(); return; }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  };

  const Tb = ({ onClick, active, disabled, children, title }: {
    onClick: () => void; active?: boolean; disabled?: boolean; children: React.ReactNode; title?: string;
  }) => (
    <Button type="button" variant="ghost" size="sm" onClick={onClick} disabled={disabled} title={title}
      className={cn("h-8 w-8 p-0", active && "bg-muted")}>
      {children}
    </Button>
  );

  const Sep = () => <div className="mx-1 h-6 w-px bg-border" />;

  const htmlArea = (
    <textarea
      value={htmlSource}
      onChange={(e) => setHtmlSource(e.target.value)}
      className="min-h-[260px] w-full resize-y rounded-b-md bg-muted/30 p-3 font-mono text-xs focus:outline-none"
      spellCheck={false}
    />
  );

  return (
    <div className={cn("rounded-md border border-input bg-card", className)}>
      <div className="flex flex-wrap items-center gap-1 border-b border-input p-2">
        {htmlMode ? (
          <>
            <span className="px-1 text-[11px] text-muted-foreground">{t("richtext.htmlMode")}</span>
            <Sep />
            <Button type="button" size="sm" variant="secondary" className="h-8 px-3 text-xs" onClick={() => exitHtmlMode(true)}>
              {t("richtext.apply")}
            </Button>
            <Button type="button" size="sm" variant="ghost" className="h-8 px-3 text-xs" onClick={() => exitHtmlMode(false)}>
              {t("common.cancel")}
            </Button>
          </>
        ) : (
          <>
            {/* The block format, in the toolbar's raised dress rather than the
                dialog's sunken one: this row stands ON the editor card, beside
                buttons, which is the case `SelectField` is for. Cut down to the
                32px height the icon buttons wear, and given a fixed width so
                the row does not reflow as the caret moves between a paragraph
                and a heading. */}
            <SelectField
              className="h-8 w-32 min-w-0 px-3 text-xs"
              value={
                editor.isActive("heading", { level: 1 }) ? "h1" :
                editor.isActive("heading", { level: 2 }) ? "h2" :
                editor.isActive("heading", { level: 3 }) ? "h3" : "p"
              }
              onChange={(v) => {
                if (v === "p") editor.chain().focus().setParagraph().run();
                else editor.chain().focus().toggleHeading({ level: parseInt(v.slice(1)) as 1 | 2 | 3 }).run();
              }}
            >
              <option value="p">{t("richtext.paragraph")}</option>
              <option value="h1">{t("richtext.h1")}</option>
              <option value="h2">{t("richtext.h2")}</option>
              <option value="h3">{t("richtext.h3")}</option>
            </SelectField>
            <Sep />
            <Tb onClick={() => editor.chain().focus().toggleBold().run()} active={editor.isActive("bold")} title={t("richtext.bold")}><Bold className="h-4 w-4" /></Tb>
            <Tb onClick={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive("italic")} title={t("richtext.italic")}><Italic className="h-4 w-4" /></Tb>
            <Tb onClick={() => editor.chain().focus().toggleUnderline().run()} active={editor.isActive("underline")} title={t("richtext.underline")}><UnderlineIcon className="h-4 w-4" /></Tb>
            <Tb onClick={() => editor.chain().focus().toggleStrike().run()} active={editor.isActive("strike")} title={t("richtext.strike")}><Strikethrough className="h-4 w-4" /></Tb>
            <Sep />
            <Tb onClick={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive("bulletList")} title={t("richtext.bulletList")}><List className="h-4 w-4" /></Tb>
            <Tb onClick={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive("orderedList")} title={t("richtext.orderedList")}><ListOrdered className="h-4 w-4" /></Tb>
            <Sep />
            <Tb onClick={() => editor.chain().focus().setTextAlign("left").run()} active={editor.isActive({ textAlign: "left" })} title={t("richtext.alignLeft")}><AlignLeft className="h-4 w-4" /></Tb>
            <Tb onClick={() => editor.chain().focus().setTextAlign("center").run()} active={editor.isActive({ textAlign: "center" })} title={t("richtext.alignCenter")}><AlignCenter className="h-4 w-4" /></Tb>
            <Tb onClick={() => editor.chain().focus().setTextAlign("right").run()} active={editor.isActive({ textAlign: "right" })} title={t("richtext.alignRight")}><AlignRight className="h-4 w-4" /></Tb>
            <Tb onClick={() => editor.chain().focus().setTextAlign("justify").run()} active={editor.isActive({ textAlign: "justify" })} title={t("richtext.alignJustify")}><AlignJustify className="h-4 w-4" /></Tb>
            <Sep />
            <Tb onClick={setLink} active={editor.isActive("link")} title={t("richtext.link")}><LinkIcon className="h-4 w-4" /></Tb>
            <Sep />
            <Tb onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().chain().focus().undo().run()} title={t("richtext.undo")}><Undo className="h-4 w-4" /></Tb>
            <Tb onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().chain().focus().redo().run()} title={t("richtext.redo")}><Redo className="h-4 w-4" /></Tb>
            <Sep />
            <Tb onClick={enterHtmlMode} title={t("richtext.html")}><Code2 className="h-4 w-4" /></Tb>
          </>
        )}
      </div>

      {after ? (
        // One body for the text and what follows it, so they scroll together.
        <div className="min-h-[260px]">
          {htmlMode ? htmlArea : <EditorContent editor={editor} />}
          {after}
        </div>
      ) : htmlMode ? (
        htmlArea
      ) : (
        <EditorContent editor={editor} className="min-h-[260px]" />
      )}
    </div>
  );
}
